import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Language } from '../core/models/language.model';
import {
   AppUserForgotPasswordRequest,
   AppUserSigninRequest,
   AppUserSignupInput,
   AppUserSignupRequest,
   AppUserTokenResponse,
} from '../models/auth.model';
import { User } from '../models/user.model';
import { AuthStore } from './auth-store.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
   private readonly clientId = environment.consumerIdentityClientId || 'pedemais';
   private readonly baseUrl = this.buildBaseUrl();
   private restoreSessionRequest?: Observable<boolean>;

   constructor(
      private http: HttpClient,
      private store: AuthStore,
      private userService: UserService,
   ) {}

   get isAuthenticated(): boolean {
      return this.store.isAuthenticated;
   }

   signup(input: AppUserSignupInput): Observable<void> {
      const request: AppUserSignupRequest = {
         ...input,
         ClientId: this.clientId,
         Origem: environment.consumerIdentityOrigin,
      };

      return this.http.post<void>(`${this.baseUrl}AppAuth/signup`, request).pipe(map(() => void 0));
   }

   confirmEmail(token: string): Observable<void> {
      return this.http.post<void>(`${this.baseUrl}AppAuth/signup/confirm`, { Token: token }).pipe(map(() => void 0));
   }

   resendConfirmation(email: string, friendlyName?: string): Observable<void> {
      return this.http
         .post<void>(`${this.baseUrl}AppAuth/signup/resend`, { Email: email, FriendlyName: friendlyName })
         .pipe(map(() => void 0));
   }

   forgotPassword(email: string, friendlyName?: string): Observable<void> {
      const request: AppUserForgotPasswordRequest = { Email: email, FriendlyName: friendlyName, Origem: environment.consumerIdentityOrigin };
      return this.http.post<void>(`${this.baseUrl}AppAuth/password/forgot`, request).pipe(map(() => void 0));
   }

   resetPassword(token: string, password: string): Observable<void> {
      return this.http
         .post<void>(`${this.baseUrl}AppAuth/password/reset`, { Token: token, Password: password })
         .pipe(map(() => void 0));
   }

   signin(email: string, password: string): Observable<AppUserTokenResponse> {
      const request: AppUserSigninRequest = { Email: email, Password: password, ClientId: this.clientId };
      return this.http
         .post<AppUserTokenResponse>(`${this.baseUrl}AppAuth/signin`, request, { withCredentials: true })
         .pipe(tap((response) => this.applySession(response)));
   }

   restoreSession(): Observable<boolean> {
      if (this.store.isAuthenticated && this.store.hasValidAccessToken) {
         this.synchronizeUserProfile(this.store.profile!);
         return of(true);
      }
      if (this.store.isInitialized && !this.store.isAuthenticated) {
         return of(false);
      }

      return this.refreshAccessToken();
   }

   /** Forces a new access token via the HttpOnly refresh cookie. Dedupes concurrent calls. */
   refreshAccessToken(): Observable<boolean> {
      this.restoreSessionRequest ??= this.http
         .post<AppUserTokenResponse>(`${this.baseUrl}AppAuth/token/refresh`, {}, { withCredentials: true })
         .pipe(
            tap((response) => this.applySession(response)),
            map(() => true),
            catchError(() => {
               this.store.clear();
               if (this.userService.getUserInfo()?.Id) {
                  this.userService.clearAuthenticatedProfile();
               }
               return of(false);
            }),
            finalize(() => (this.restoreSessionRequest = undefined)),
            shareReplay({ bufferSize: 1, refCount: false }),
         );

      return this.restoreSessionRequest;
   }

   signout(): Observable<void> {
      return this.http.post<void>(`${this.baseUrl}AppAuth/signout`, {}, { withCredentials: true }).pipe(
         tap(() => this.clearSession()),
         catchError(() => {
            this.clearSession();
            return of(void 0);
         }),
      );
   }

   changePassword(currentPassword: string, newPassword: string): Observable<void> {
      return this.http
         .post<void>(
            `${this.baseUrl}AppAuth/password/change`,
            {
               CurrentPassword: currentPassword,
               NewPassword: newPassword,
            },
            { withCredentials: true },
         )
         .pipe(map(() => void 0));
   }

   private clearSession(): void {
      this.store.clear();
      this.userService.clearAuthenticatedProfile();
   }

   private applySession(response: AppUserTokenResponse): void {
      if (!response?.AccessToken || !response?.Profile) {
         throw new Error('Invalid session');
      }

      this.synchronizeUserProfile(response.Profile);
      this.store.setSession(response.AccessToken, response.Profile);
   }

   private synchronizeUserProfile(profile: AppUserTokenResponse['Profile']): void {
      if (!profile) {
         return;
      }

      const current = this.userService.getUserInfo();
      const language = current.Language || new Language();
      language.Key = profile.Language || language.Key;

      const user = new User();
      user.Id = profile.Id || '';
      user.Email = profile.Email || '';
      user.Name = profile.Name || '';
      user.CellPhone = profile.CellPhone || '';
      user.DocumentNumber = profile.DocumentNumber || '';
      user.BirthDate = profile.BirthDate || '';
      user.PhotoUrl = profile.PhotoUrl || current.PhotoUrl || user.PhotoUrl;
      user.MemberSince = profile.MemberSince || current.MemberSince || user.MemberSince;
      user.Language = language;
      this.userService.replaceAuthenticatedProfile(user);
   }

   private buildBaseUrl(): string {
      const root = environment.apiBaseUrl.endsWith('/') ? environment.apiBaseUrl : `${environment.apiBaseUrl}/`;
      const prefix = (environment.apiPathPrefix || 'api/v1').replace(/^\/+|\/+$/g, '');
      return `${root}${prefix}/`;
   }
}
